<script setup>
defineProps({
  headerTitle: {
    type: String,
    default: '',
  },
  headerDescription: {
    type: String,
    default: '',
  },
  headerIcon: {
    type: String,
    default: 'i-ph-paper-plane-tilt-bold',
  },
  buttonLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['click', 'close']);

const handleButtonClick = () => {
  emit('click');
};
</script>

<template>
  <section class="campaign-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header class="campaign-header flex-shrink-0">
      <div class="header-bg">
        <div class="header-glow" />
        <div class="header-pattern" />
      </div>
      <div class="header-inner">
        <div class="header-left">
          <div class="header-icon-box">
            <span :class="headerIcon" class="size-5" />
          </div>
          <div class="header-text">
            <h1 class="header-title">{{ headerTitle }}</h1>
            <p v-if="headerDescription" class="header-desc">
              {{ headerDescription }}
            </p>
          </div>
        </div>
        <div class="header-actions">
          <slot name="header-actions-left" />
          <div
            v-on-clickaway="() => emit('close')"
            class="relative group/campaign-button"
          >
            <button
              v-if="buttonLabel"
              type="button"
              class="tm__btn tm__btn--primary"
              @click="handleButtonClick"
            >
              <span class="i-ph-plus-bold size-4" />
              {{ buttonLabel }}
            </button>
            <slot name="action" />
          </div>
        </div>
      </div>
    </header>

    <!-- Toolbar -->
    <div v-if="$slots.toolbar" class="campaign-toolbar">
      <slot name="toolbar" />
    </div>

    <!-- Content -->
    <main class="campaign-content flex-1 overflow-y-auto">
      <slot name="default" />
    </main>
  </section>
</template>

<style scoped>
/* Force light mode for all campaign pages */
.campaign-page,
.dark .campaign-page {
  background: #ffffff !important;
  --wlab-bg-primary: #ffffff;
  --wlab-bg-card: #ffffff;
  --wlab-bg-input: #f9fafb;
  --wlab-bg-hover: #f3f4f6;
  --wlab-bg-secondary: #f9fafb;
  --wlab-bg-tertiary: #f3f4f6;
  --wlab-border-light: #e5e7eb;
  --wlab-border-default: #e5e7eb;
  --wlab-text-primary: #111827;
  --wlab-text-secondary: #374151;
  --wlab-text-muted: #9ca3af;
  --wlab-text-subtle: #6b7280;
  --wlab-text-link: #0066ff;
  --wlab-text-link-hover: #ea6b00;
  --wlab-accent: #0066ff;
}

/* Override dark gradient backgrounds to white */
.campaign-page :deep(.dark\:from-n-background),
.campaign-page :deep(.from-n-background) {
  --tw-gradient-from: #ffffff var(--tw-gradient-from-position) !important;
}

.campaign-page :deep(.bg-n-background),
.campaign-page :deep(.dark\:bg-n-background),
.campaign-page :deep(.bg-n-solid-1),
.campaign-page :deep(.bg-n-solid-2),
.campaign-page :deep(.bg-n-solid-3) {
  background-color: #ffffff !important;
}

.campaign-page :deep(.text-n-slate-12) {
  color: #111827 !important;
}
.campaign-page :deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
.campaign-page :deep(.text-n-slate-10) {
  color: #6b7280 !important;
}

.campaign-page :deep(.border-n-weak),
.campaign-page :deep(.border-n-strong),
.campaign-page :deep(.divide-n-weak > * + *) {
  border-color: #e5e7eb !important;
}

/* Header */
.campaign-header {
  position: relative;
  padding: 12px 32px;
  background: #ffffff !important;
  border-bottom: 1px solid #e5e7eb;
  overflow: hidden;
}

.header-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.header-glow {
  position: absolute;
  top: -40px;
  right: 60px;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(99, 102, 241, 0.06) 0%,
    transparent 70%
  );
}

.header-pattern {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
    rgba(99, 102, 241, 0.04) 1px,
    transparent 1px
  );
  background-size: 20px 20px;
}

.header-inner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.header-icon-box {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.15) 0%,
    rgba(139, 92, 246, 0.08) 100%
  );
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #818cf8;
}

.header-text {
  min-width: 0;
}

.header-title {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #111827;
  line-height: 1.2;
  margin: 0;
}

.header-desc {
  font-size: 11px;
  color: #6b7280;
  margin: 2px 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

/* Toolbar */
.campaign-toolbar {
  padding: 10px 32px;
  background: #ffffff !important;
  border-bottom: 1px solid #e5e7eb;
}

/* Content */
.campaign-content {
  background: #ffffff !important;
}
</style>
